import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { browserHistory } from 'react-router';
import * as actions from '../../actions';
import * as Constants from '../../constants';
import { IMAGE_URL_PREFIX, ROUTE_PATHS } from '../../constants';
import { getServiceByRoutePrefix } from '../../constants/services';
import BookCard from './components/BookCard/BookCard';
import FooterButtons from '../../components/Button/FooterButtons';
import Drawer from '../../components/Drawer/Drawer';
import './ServiceSelectPackage.scss';

const staticText = {
  serviceNotFound: 'Service not found',
  loadingService: 'Loading service...',
  selectPackage: 'Select a Package',
  loadingPackages: 'Loading packages...',
  errorPackagesPrefix: 'Error loading packages: ',
  emptyPackages: 'No packages available for this service at the moment.',
  backBtn: 'Back to Customer Info',
  proceedBtn: 'Proceed to Slot & Patient',
  cancelBtn: 'Cancel Order',
  sessionsFallback: 'Sessions',
  closeBtn: '✕',
  loadingOptions: 'Loading options...',
  mostBookedFallback: 'Most Booked',
};

class ServiceSelectPackage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      pendingPackage: null,
      variantDrawerOpen: false,
    };
  }

  componentDidMount() {
    const { params, actions, servicePlatform } = this.props;
    const { serviceType } = params;

    const service = getServiceByRoutePrefix(serviceType);
    if (!service) {
      alert(staticText.serviceNotFound);
      browserHistory.push({ pathname: '/customer-info', state: { alreadyVisitedCustomerInfoPage: true } });
      return;
    }

    // If service ID changed, reset the entire service platform state to start fresh
    if (servicePlatform.service.serviceId !== service.serviceId) {
      actions.resetServicePlatform();
    }

    actions.setServiceDetails(service);
    actions.changeHeaderState(Constants.SERVICE_PLATFORM_SELECT_PACKAGE);
    actions.fetchCategoriesSubCategories(service.serviceId);
  }

  componentDidUpdate(prevProps) {
    const { servicePlatform, actions, params } = this.props;
    const { categoriesData, packages, itemVariants } = servicePlatform;
    const { pendingPackage } = this.state;

    // Detect service type change - reset and fetch new service data
    if (prevProps.params.serviceType !== params.serviceType) {
      const service = getServiceByRoutePrefix(params.serviceType);
      if (service) {
        actions.resetServicePlatform();
        actions.setServiceDetails(service);
        actions.changeHeaderState(Constants.SERVICE_PLATFORM_SELECT_PACKAGE);
        actions.fetchCategoriesSubCategories(service.serviceId);
        return;
      }
    }

    if (categoriesData && !prevProps.servicePlatform.categoriesData &&
        !packages.loading && packages.data.length === 0) {
      this.fetchAllPackages(categoriesData);
    }

    // Variants loaded — decide whether to open drawer or auto-select
    if (prevProps.servicePlatform.itemVariants.loading && !itemVariants.loading && pendingPackage) {
      if (itemVariants.data.length > 1) {
        this.setState({ variantDrawerOpen: true });
      } else {
        actions.setSelectedServicePackage(pendingPackage);
        this.setState({ pendingPackage: null });
      }
    }
  }

  fetchAllPackages = (categoriesData) => {
    const { dispatch, actions } = this.props;

    const allCategory = (categoriesData.categories || []).find(category => category.categoryId === -1);

    if (!allCategory || !allCategory.subcategories || allCategory.subcategories.length === 0) {
      console.error('No subcategories found in ALL category');
      return;
    }

    const subcategories = allCategory.subcategories.filter(
      (subcategory, idx, self) => idx === self.findIndex(category => category.id === subcategory.id)
    );

    dispatch({
      type: Constants.FETCH_SERVICE_PACKAGES_BATCH_START,
      total: subcategories.length,
    });

    subcategories.forEach(sub => {
      actions.fetchServicePackagesBatchItem(sub.categoryId, sub.id);
    });
  }

  handlePackageSelect = (packageItem) => {
    this.setState({ pendingPackage: packageItem });
    this.props.actions.fetchServiceItemVariants(packageItem.id);
  }

  handleVariantConfirmForId = (variantId) => {
    const { servicePlatform, actions } = this.props;
    const { pendingPackage } = this.state;
    const variant = servicePlatform.itemVariants.data.find(item => String(item.id) === variantId);
    actions.setSelectedServicePackage(variant || pendingPackage);
    browserHistory.push(ROUTE_PATHS.SERVICE_SLOT_PATIENT(this.props.params.serviceType));
  }

  handleDrawerClose = () => {
    this.setState({ variantDrawerOpen: false, pendingPackage: null, selectedVariantId: null });
  }

  handleProceed = () => {
    const { params } = this.props;
    browserHistory.push(ROUTE_PATHS.SERVICE_SLOT_PATIENT(params.serviceType));
  }

  render() {
    const { servicePlatform } = this.props;
    const { service, packages, selectedPackage, itemVariants } = servicePlatform;
    const { variantDrawerOpen } = this.state;

    if (!service || !service.serviceId) {
      return <div className="serviceSelectPackage">{staticText.loadingService}</div>;
    }

    return (
      <div className="serviceSelectPackageWrapper">
        <div className="serviceSelectPackage">
          <p className="serviceTitle">{staticText.selectPackage}</p>

          {packages.loading && (
            <div className="loadingState">{staticText.loadingPackages}</div>
          )}

          {packages.error && (
            <div className="errorState">
              {staticText.errorPackagesPrefix}{packages.error}
            </div>
          )}

          {!packages.loading && !packages.error && packages.data && packages.data.length === 0 && (
            <div className="emptyState">
              {staticText.emptyPackages}
            </div>
          )}

          {packages.data && packages.data.length > 0 && (
            <div className="packagesGrid">
              {packages.data.map(packageItem => (
                <BookCard
                  key={packageItem.id}
                  title={packageItem.name}
                  visitType={packageItem.attributes && packageItem.attributes.visitTypeWithSessionsKey}
                  sessionCount={packageItem.sessions}
                  originalPrice={packageItem.mrp}
                  amountToPay={packageItem.sellingPrice}
                  bullets={packageItem.attributes && packageItem.attributes.whatsIncluded || []}
                  tickIcon={`${IMAGE_URL_PREFIX}service-platform/icon_Tick_Black.png`}
                  imageIcon={packageItem.attributes && packageItem.attributes.imageURL ? packageItem.attributes.imageURL : `${IMAGE_URL_PREFIX}service-platform/placeholder.png`}
                  isSelected={!!(selectedPackage && selectedPackage.id === packageItem.id)}
                  onCardClick={() => this.handlePackageSelect(packageItem)}
                />
              ))}
            </div>
          )}
        </div>

        <FooterButtons
          backBtnText={staticText.backBtn}
          backBtnClick={() => browserHistory.push({ pathname: '/customer-info', state: { alreadyVisitedCustomerInfoPage: true } })}
          proceedBtnText={staticText.proceedBtn}
          proceedBtnClick={this.handleProceed}
          nextStateAllowed={!!selectedPackage}
          cancelBtnText={staticText.cancelBtn}
          cancelBtnClick={() => browserHistory.push({ pathname: '/customer-info', state: { alreadyVisitedCustomerInfoPage: true } })}
        />

        <Drawer
          open={variantDrawerOpen}
          position="right"
          onClose={this.handleDrawerClose}
          showCloseButton={false}
        >
          <div className="variantDrawer">
            <div className="variantDrawerHeader">
              <p className="variantDrawerTitle">
                Select {itemVariants.data.length > 0 && itemVariants.data[0].attributes && itemVariants.data[0].attributes.variantsText || staticText.sessionsFallback}
              </p>
              <button className="variantDrawerClose" onClick={this.handleDrawerClose}>{staticText.closeBtn}</button>
            </div>

            <div className="variantDrawerList">
              {itemVariants.loading ? (
                <p className="variantDrawerLoading">{staticText.loadingOptions}</p>
              ) : (
                itemVariants.data.map(variant => {
                  const attrs = variant.attributes || {};
                  return (
                    <div
                      key={variant.id}
                      className="variantOption"
                      onClick={() => this.handleVariantConfirmForId(String(variant.id))}
                    >
                      {attrs.isConsultationFree && attrs.consultationInfo && (
                        <div className="variantConsultBadge">
                          <img
                            src={`${IMAGE_URL_PREFIX}service-platform/icon_Gift_Box.png`}
                            alt="gift"
                            width={14}
                            height={14}
                          />
                          {attrs.consultationInfo}
                        </div>
                      )}
                      <span className="variantRadio" />
                      <div className="variantContent">
                        <div className={'variantInfoLeft' + (!attrs.visitDetails ? ' variantInfoLeftCentered' : '')}>
                          <span className="variantSessions">{variant.sessions} {attrs.variantsText || staticText.sessionsFallback}</span>
                          {attrs.visitDetails && (
                            <span className="variantSubtitle">{attrs.visitDetails}</span>
                          )}
                        </div>
                        <div className="variantInfoRight">
                          <div className="variantPriceRow">
                            {variant.mrp > variant.sellingPrice && (
                              <span className="variantMrp">₹{variant.mrp}</span>
                            )}
                            <span className="variantPrice">₹{variant.sellingPrice}</span>
                          </div>
                          {variant.perSessionPrice > 0 && (
                            <span className="variantPerSession">₹{variant.perSessionPrice}/{attrs.variantsText || staticText.sessionsFallback}</span>
                          )}
                        </div>
                      </div>
                      {attrs.mostBooked && (
                        <div className="variantMostBooked">
                          <img
                            src={`${IMAGE_URL_PREFIX}service-platform/icon_Star_Blue.png`}
                            alt="star"
                            width={12}
                            height={12}
                          />
                          {attrs.mostBookedText || staticText.mostBookedFallback}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </Drawer>
      </div>
    );
  }
}

ServiceSelectPackage.propTypes = {
  params: PropTypes.object.isRequired,
  servicePlatform: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default connect(
  state => ({ servicePlatform: state.servicePlatform }),
  dispatch => ({
    actions: bindActionCreators(actions, dispatch),
    dispatch: dispatch,
  })
)(ServiceSelectPackage);
